import { BodyText, Callout } from "@/components/ui";
import { RxHistoryListSkeleton } from "@/features/Patient/MedicalRecords/RxHistory/RxHistoryListSkeleton";
import { RxHistoryMedicationCard } from "@/features/Patient/MedicalRecords/RxHistory/RxHistoryMedicationCard";
import type { RxHistoryMedication } from "@/features/Patient/MedicalRecords/RxHistory/types";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";

interface RxHistoryMedicationListProps {
  medications: RxHistoryMedication[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
}

export const RxHistoryMedicationList = ({
  medications,
  isLoading,
  isError,
  onRetry,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
}: RxHistoryMedicationListProps) => {
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && hasNextPage && fetchNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, fetchNextPage]);

  if (isLoading) {
    return <RxHistoryListSkeleton />;
  }

  if (isError) {
    return (
      <Callout
        className="w-full cursor-pointer"
        variant="danger"
        title="Unable to load Surescripts medication history!"
        description="Please click here to try again."
        onClick={onRetry}
      />
    );
  }

  if (medications.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <BodyText size="sm" textColor="tertiary">
          No Surescripts network medication history found
        </BodyText>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {medications.map((medication) => (
        <RxHistoryMedicationCard
          key={medication.patientVendorMedicationHistoryId}
          medication={medication}
        />
      ))}

      {/* Loading indicator */}
      {isFetchingNextPage && (
        <div className="flex justify-center py-2">
          <BodyText size="sm" textColor="tertiary">
            Loading more medications...
          </BodyText>
        </div>
      )}

      {/* Sentinel element for infinite scroll */}
      {(isFetchingNextPage || hasNextPage) && (
        <div ref={hasNextPage ? ref : undefined} style={{ height: "1px" }} />
      )}
    </div>
  );
};
